import { z } from "zod";

import type { GenerationResponse } from "../openai/openai.models";
import {
  EmbeddingRequestPayloadSchema,
  V1ResponsesCreateRequestSchema,
} from "../openai";
import {
  V1ChatCompletionRequestSchema,
  V1TextCompletionRequestSchema,
} from "../openai/completions/completions.models";
import {
  GenerationRequestSchema,
  GenerationResponseSchema,
} from "../openai/openai.models";
import { authedServiceRequest } from "./BaseService";
import { pageInputSchema } from "./pagination.models";

/** ******************************************************************************
 * NOTE:
 * The types in this file are defined as a combination of OpenAI API compatible
 * types and our internal types.
 *
 * For example, snake case in responses types is because we are trying to match
 * OpenAI response types.
 *
 * However, note that the types are not identical everywhere (e.g. some properties
 * present in OpenAI request/responses do not exist in our types).
 *
 * Docs:
 * https://platform.openai.com/docs/guides/batch
 * https://platform.openai.com/docs/api-reference/batch
 * https://platform.openai.com/docs/api-reference/files
 ******************************************************************************* */

/** ******************************************************************************
 *  Types
 ******************************************************************************* */

export const BatchInference = z.object({
  _id: z.string(),
  teamId: z.string(),
  projectId: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  inputFileId: z.string().nullable(),
  outputFileId: z.string().nullable(),
  errorFileId: z.string().nullable(),
  inProgressAt: z.date().nullable(),
  failedAt: z.date().nullable(),
  expiredAt: z.date().nullable(),
  finalizingAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
  cancellingAt: z.date().nullable(),
  errors: z.array(z.string()).nullable(),
  completionWindow: z.string(),
  expiresAt: z.date(),
  failedCount: z.number(),
  completedCount: z.number(),
  batchSize: z.number(),
  isGroup: z.boolean(),
  metadata: z.record(z.string(), z.any()).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type BatchInference = z.infer<typeof BatchInference>;

export const CreateBatchInferenceInput = BatchInference.omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  inputFileId: z.string(), // This makes inputFileId required
});

export type CreateBatchInferenceInput = z.infer<
  typeof CreateBatchInferenceInput
>;

export const OpenAIBatchSchema = z.object({
  id: z.string(),
  object: z.literal("batch"),
  endpoint: z.enum(["/v1/chat/completions", "/v1/completions"]),
  errors: z
    .object({
      object: z.literal("list"),
      data: z.array(
        z.object({
          code: z.string(),
          message: z.string(),
          param: z.string().nullable(),
          line: z.number().nullable(),
        }),
      ),
    })
    .nullable(),
  completion_window: z
    .string()
    .endsWith("h")
    .or(z.string().endsWith("m"))
    .or(z.string().endsWith("s")),
  input_file_id: z.string(),
  status: z.enum([
    "validating",
    "in_progress",
    "failed",
    "completed",
    "expired",
    "cancelled",
    "cancelling",
    "finalizing",
  ]),
  output_file_id: z.string().nullable(),
  error_file_id: z.string().nullable(),
  created_at: z.number(),
  in_progress_at: z.number().nullable(),
  expires_at: z.number().nullable(),
  finalizing_at: z.number().nullable(),
  completed_at: z.number().nullable(),
  failed_at: z.number().nullable(),
  expired_at: z.number().nullable(),
  cancelling_at: z.number().nullable(),
  cancelled_at: z.number().nullable(),
  request_counts: z.object({
    total: z.number(),
    completed: z.number(),
    failed: z.number(),
  }),
  metadata: z.record(z.string().max(64), z.string().max(512)).nullable(),
});

export type OpenAIBatch = z.infer<typeof OpenAIBatchSchema>;

const BaseBatchInputRequestObjectSchema = z.object({
  custom_id: z.string(),
  method: z.literal("POST"),
});

const ChatCompletionBatchInputRequestSchema =
  BaseBatchInputRequestObjectSchema.merge(
    z.object({
      body: V1ChatCompletionRequestSchema,
    }),
  );

const CompletionBatchInputRequestSchema =
  BaseBatchInputRequestObjectSchema.merge(
    z.object({
      body: V1TextCompletionRequestSchema,
    }),
  );

const EmbeddingBatchInputRequestSchema =
  BaseBatchInputRequestObjectSchema.merge(
    z.object({
      body: EmbeddingRequestPayloadSchema,
    }),
  );

export const BatchInputRequestObjectSchema =
  ChatCompletionBatchInputRequestSchema.or(
    CompletionBatchInputRequestSchema,
  ).or(EmbeddingBatchInputRequestSchema);

export type BatchInputRequestObject = z.infer<
  typeof BatchInputRequestObjectSchema
>;

/** ******************************************************************************
 *  Service Interface
 ******************************************************************************* */

export type BatchServiceInterface = {
  getCachedStatusCounts: (batchId: string) => Promise<{
    succeeded: number;
    failed: number;
  }>;
  uploadBatchFile: (params: {
    teamId: string;
    projectId: string;
    file: Blob;
    fileName: string;
    mimetype: string;
  }) => Promise<OpenAIFile>;
  initiateBatchFileMultipartUpload: (
    params: InitiateBatchFileMultipartUploadParams,
  ) => Promise<InitiateBatchFileMultipartUploadResponse>;
  uploadBatchFileMultipartPart: (
    params: UploadBatchFileMultipartPartParams,
  ) => Promise<UploadBatchFileMultipartPartResponse>;
  completeBatchFileMultipartUpload: (
    params: CompleteBatchFileMultipartUploadParams,
  ) => Promise<OpenAIFile>;
  abortBatchFileMultipartUpload: (
    params: AbortBatchFileMultipartUploadParams,
  ) => Promise<AbortBatchFileMultipartUploadResponse>;
  createBatch: (request: CreateBatchRequest) => Promise<CreateBatchResponse>;
  retrieveBatch: (
    request: RetrieveBatchRequest,
  ) => Promise<RetrieveBatchResponse>;
  getBatches: (request: GetBatchesRequest) => Promise<GetBatchesResponse>;
  listBatches: (request: ListBatchesRequest) => Promise<ListBatchesResponse>;
  listFiles: (request: ListFilesRequest) => Promise<ListFilesResponse>;
  retrieveFile: (request: FileIdRequest) => Promise<OpenAIFile>;
  deleteFile: (request: FileIdRequest) => Promise<DeleteFileResponse>;

  getBatchFileUrl: (
    request: GetBatchFileUrlRequest,
  ) => Promise<GetBatchFileUrlResponse>;
  finishBatch: (request: FinishBatchParams) => Promise<void>;
  cancelBatch: (request: CancelBatchRequest) => Promise<RetrieveBatchResponse>;
  failBatch: (request: FailBatchParams) => Promise<BatchInference | null>;
  createGroupBatch: (
    params: CreateGroupBatchParams,
  ) => Promise<CreateGroupBatchResponse>;
  storage: BatchStorageInterface;
};

/**
 * Slow Groups
 */

export const CreateGroupBatchParamsSchema = z.object({
  requests: z.array(GenerationRequestSchema),
  teamId: z.string(),
  projectId: z.string(),
  webhookId: z.string().optional(),
  endpoint: z.enum(["/v1/chat/completions", "/v1/completions"]),
});

export type CreateGroupBatchParams = z.infer<
  typeof CreateGroupBatchParamsSchema
>;

export const CreateGroupBatchResponseSchema = z.object({
  groupId: z.string(),
  groupSize: z.number(),
});

export type CreateGroupBatchResponse = z.infer<
  typeof CreateGroupBatchResponseSchema
>;

/** ******************************************************************************
 *  Storage Interface
 ******************************************************************************* */

export type BatchStorageInterface = {
  create: (batch: CreateBatchInferenceInput) => Promise<BatchInference>;
  byId: (id: string) => Promise<BatchInference | null>;
  byTeamId: (
    teamId: string,
    projectId: string | null,
  ) => Promise<BatchInference[]>;
  byInputFileId: (fileId: string) => Promise<BatchInference | null>;
  listBatches: (
    teamId: string,
    projectId: string | null,
    limit: number,
    after?: string,
  ) => Promise<{ batches: BatchInference[]; hasMore: boolean } | null>;
  outputFileIds: (teamId: string, fileIds: string[]) => Promise<Set<string>>;
  update: (
    id: string,
    batch: Partial<BatchInference>,
  ) => Promise<BatchInference>;
  getBatchesForFileExpiration: () => Promise<BatchInference[]>;
};

/** ******************************************************************************
 *  Upload Batch File
 ******************************************************************************* */

export const filePurpose = z.enum(["batch", "batch_output"]);
export type FilePurpose = z.infer<typeof filePurpose>;

export const fileListOrder = z.enum(["asc", "desc"]);
export type FileListOrder = z.infer<typeof fileListOrder>;

/** OpenAI file object. `created_at` is Unix seconds. */
export const OpenAIFileSchema = z.object({
  id: z.string(),
  object: z.literal("file"),
  bytes: z.number(),
  created_at: z.number(),
  filename: z.string(),
  purpose: filePurpose,
  status: z.literal("processed"),
});

export type OpenAIFile = z.infer<typeof OpenAIFileSchema>;

export const listFilesParams = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  after: z.string().optional(),
  purpose: filePurpose.optional(),
  order: fileListOrder.default("desc"),
});

export const listFilesRequest = authedServiceRequest.merge(listFilesParams);

export const listFilesResponse = z.object({
  object: z.literal("list"),
  data: OpenAIFileSchema.array(),
  first_id: z.string(),
  last_id: z.string(),
  has_more: z.boolean(),
});

export const fileIdRequest = authedServiceRequest.merge(
  z.object({ params: z.object({ fileId: z.string() }) }),
);

export const deleteFileResponse = z.object({
  id: z.string(),
  object: z.literal("file"),
  deleted: z.literal(true),
});

export type ListFilesParams = z.infer<typeof listFilesParams>;
export type ListFilesRequest = z.infer<typeof listFilesRequest>;
export type ListFilesResponse = z.infer<typeof listFilesResponse>;
export type FileIdRequest = z.infer<typeof fileIdRequest>;
export type DeleteFileResponse = z.infer<typeof deleteFileResponse>;

export const batchFileMultipartUploadPart = z.object({
  partNumber: z.number().int().positive(),
  etag: z.string(),
});

export type BatchFileMultipartUploadPart = z.infer<
  typeof batchFileMultipartUploadPart
>;

export type InitiateBatchFileMultipartUploadParams = {
  teamId: string;
  fileName: string;
  mimetype: string;
  fileSize: number;
};

export const initiateBatchFileMultipartUploadResponse = z.object({
  uploadId: z.string(),
  r2UploadId: z.string(),
  r2Key: z.string(),
  partSize: z.number(),
  totalParts: z.number(),
});

export type InitiateBatchFileMultipartUploadResponse = z.infer<
  typeof initiateBatchFileMultipartUploadResponse
>;

export type UploadBatchFileMultipartPartParams = {
  teamId: string;
  r2UploadId: string;
  r2Key: string;
  partNumber: number;
  body: ReadableStream<Uint8Array>;
};

export const uploadBatchFileMultipartPartResponse = z.object({
  partNumber: z.number(),
  etag: z.string(),
});

export type UploadBatchFileMultipartPartResponse = z.infer<
  typeof uploadBatchFileMultipartPartResponse
>;

export type CompleteBatchFileMultipartUploadParams = {
  teamId: string;
  projectId: string;
  r2UploadId: string;
  r2Key: string;
  parts: BatchFileMultipartUploadPart[];
};

export const abortBatchFileMultipartUploadResponse = z.object({
  aborted: z.literal(true),
});

export type AbortBatchFileMultipartUploadParams = {
  teamId: string;
  r2UploadId: string;
  r2Key: string;
};

export type AbortBatchFileMultipartUploadResponse = z.infer<
  typeof abortBatchFileMultipartUploadResponse
>;

/** ******************************************************************************
 *  Start Batch Job
 ******************************************************************************* */

export const createBatchParams = z.object({
  input_file_id: z.string(),
  webhook_url: z.string().optional(),
  metadata: z.any().optional(),
  endpoint: z.enum(["/v1/chat/completions", "/v1/completions"]).optional(),
  completion_window: z
    .string()
    .endsWith("h")
    .or(z.string().endsWith("m"))
    .or(z.string().endsWith("s"))
    .optional()
    .default("24h"),
});

export const createBatchRequest = authedServiceRequest.merge(
  z.object({
    params: createBatchParams,
  }),
);

export const createBatchResponse = OpenAIBatchSchema;

export type CreateBatchParams = z.infer<typeof createBatchParams>;
export type CreateBatchRequest = z.infer<typeof createBatchRequest>;
export type CreateBatchResponse = z.infer<typeof createBatchResponse>;

/** ******************************************************************************
 *  Retrieve Batch
 ******************************************************************************* */

export const retrieveBatchParams = z.object({
  batchId: z.string(),
});

export const retrieveBatchRequest = authedServiceRequest.merge(
  z.object({
    params: retrieveBatchParams,
  }),
);

export const retrieveBatchResponse = OpenAIBatchSchema;

export type RetrieveBatchParams = z.infer<typeof retrieveBatchParams>;
export type RetrieveBatchRequest = z.infer<typeof retrieveBatchRequest>;
export type RetrieveBatchResponse = z.infer<typeof retrieveBatchResponse>;

/** ******************************************************************************
 *  Cancel Batch
 ******************************************************************************* */

export const cancelBatchRequest = retrieveBatchRequest;

export type CancelBatchRequest = z.infer<typeof cancelBatchRequest>;

/** ******************************************************************************
 *  Get Batches
 ******************************************************************************* */

export const getBatchesParams = z.object({});

export const getBatchesRequest = authedServiceRequest.merge(
  z.object({
    params: getBatchesParams,
  }),
);

export const getBatchesResponse = z.object({
  batches: OpenAIBatchSchema.array(),
});

export type GetBatchesParams = z.infer<typeof getBatchesParams>;
export type GetBatchesRequest = z.infer<typeof getBatchesRequest>;
export type GetBatchesResponse = z.infer<typeof getBatchesResponse>;

/** ******************************************************************************
 *  List Batches
 ******************************************************************************* */

export const listBatchesParams = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  after: z.string().optional(),
});

export const listBatchesRequest = authedServiceRequest.merge(listBatchesParams);

export const listBatchesResponse = z.object({
  object: z.literal("list"),
  data: OpenAIBatchSchema.array(),
  first_id: z.string(),
  last_id: z.string(),
  has_more: z.boolean(),
});

export type ListBatchesParams = z.infer<typeof listBatchesParams>;
export type ListBatchesRequest = z.infer<typeof listBatchesRequest>;
export type ListBatchesResponse = z.infer<typeof listBatchesResponse>;

/** ******************************************************************************
 *  Dashboard Batch List (internal tRPC only — not part of the OpenAI API)
 ******************************************************************************* */

export const BATCH_STATUSES = OpenAIBatchSchema.shape.status.options;
export const batchStatusSchema = OpenAIBatchSchema.shape.status;
export type BatchStatus = z.infer<typeof batchStatusSchema>;

/** Filters shared by the dashboard list and its status summary. */
export const dashboardBatchFiltersSchema = z.object({
  /** Narrows to a project's batches plus legacy project-less ones; null is team-wide. */
  projectId: z.string().min(1).nullish(),
  /** Unix seconds, inclusive. */
  createdAfter: z.number().int().nonnegative().optional(),
  /** Unix seconds, exclusive. */
  createdBefore: z.number().int().nonnegative().optional(),
  /** Substring of the batch id or of the serialized metadata. */
  search: z.string().trim().max(200).optional(),
});

export const listDashboardBatchesParams = pageInputSchema
  .merge(dashboardBatchFiltersSchema)
  .extend({
    statuses: z.array(batchStatusSchema).max(BATCH_STATUSES.length).optional(),
  });

export const listDashboardBatchesRequest = authedServiceRequest.merge(
  z.object({ params: listDashboardBatchesParams }),
);

export const DashboardBatchSchema = OpenAIBatchSchema.extend({
  /** Model of the batch's first dispatched line; null until one is dispatched. */
  model: z.string().nullable(),
});

export const listDashboardBatchesResponse = z.object({
  items: DashboardBatchSchema.array(),
  total: z.number(),
});

export const getDashboardBatchSummaryRequest = authedServiceRequest.merge(
  z.object({ params: dashboardBatchFiltersSchema }),
);

export const getDashboardBatchSummaryResponse = z.object({
  counts: z.array(z.object({ status: batchStatusSchema, count: z.number() })),
});

export type DashboardBatchFilters = z.infer<typeof dashboardBatchFiltersSchema>;
export type ListDashboardBatchesParams = z.infer<
  typeof listDashboardBatchesParams
>;
export type ListDashboardBatchesRequest = z.infer<
  typeof listDashboardBatchesRequest
>;
export type DashboardBatch = z.infer<typeof DashboardBatchSchema>;
export type ListDashboardBatchesResponse = z.infer<
  typeof listDashboardBatchesResponse
>;
export type GetDashboardBatchSummaryRequest = z.infer<
  typeof getDashboardBatchSummaryRequest
>;
export type GetDashboardBatchSummaryResponse = z.infer<
  typeof getDashboardBatchSummaryResponse
>;

/** ******************************************************************************
 *  Finish Batch
 ******************************************************************************* */

export const batchFinalizerPayloadSchema = z.object({
  batchId: z.string(),
});

export type BatchFinalizerPayload = z.infer<typeof batchFinalizerPayloadSchema>;

/**
 * Terminal state a finalizer writes: `completed` when every line finished,
 * `expired`, `cancelled` or `failed` when the batch closes with the lines done
 * so far.
 */
export const BatchFinishStatusSchema = z.enum([
  "completed",
  "expired",
  "cancelled",
  "failed",
]);

export type BatchFinishStatus = z.infer<typeof BatchFinishStatusSchema>;

export const finishBatchParams = z.object({
  batchId: z.string(),
  status: BatchFinishStatusSchema,
  /** With status `failed`, the error recorded on the batch. */
  reason: z.string().optional(),
  successFileId: z.string(),
  failedFileId: z.string(),
  successCount: z.number(),
  failedCount: z.number(),
  notify: z.boolean().optional().nullable().default(true),
});

export const finishBatchRequest = authedServiceRequest.merge(
  z.object({
    params: finishBatchParams,
  }),
);

export type FinishBatchParams = z.infer<typeof finishBatchParams>;
export type FinishBatchRequest = z.infer<typeof finishBatchRequest>;

/** ******************************************************************************
 *  Fail Batch
 ******************************************************************************* */

export const failBatchParams = z.object({
  batchId: z.string(),
  reason: z.string().optional(),
  /** A finalize claim set at or after this time is live; that finalizer keeps the batch. */
  finalizeClaimStaleBefore: z.date(),
});

export const failBatchRequest = authedServiceRequest.merge(
  z.object({
    params: failBatchParams,
  }),
);

export type FailBatchParams = z.infer<typeof failBatchParams>;
export type FailBatchRequest = z.infer<typeof failBatchRequest>;

/** Most requests a slow-lane group may contain. */
export const SLOW_GROUP_MAX_SIZE = 50;

/** ******************************************************************************
 *  Get Batch File URL
 ******************************************************************************* */

export const getBatchFileUrlParams = z.object({
  fileId: z.string(),
});

export const getBatchFileUrlRequest = authedServiceRequest.merge(
  z.object({
    params: getBatchFileUrlParams,
  }),
);

export const getBatchFileUrlResponse = z.object({
  url: z.string(),
});

export type GetBatchFileUrlParams = z.infer<typeof getBatchFileUrlParams>;
export type GetBatchFileUrlRequest = z.infer<typeof getBatchFileUrlRequest>;
export type GetBatchFileUrlResponse = z.infer<typeof getBatchFileUrlResponse>;

/** ******************************************************************************
 *  Batch Output File Formats
 ******************************************************************************* */

export const SuccessfulBatchInferenceJSONLineSchema = z.object({
  id: z.string(),
  custom_id: z.string(),
  response: z.object({
    status_code: z.number(),
    request_id: z.string(),
    body: GenerationResponseSchema,
  }),
  error: z.string().nullable(),
});

export type SuccessfulBatchInferenceJSONLine = z.infer<
  typeof SuccessfulBatchInferenceJSONLineSchema
>;

export const FailedBatchInferenceJSONLineSchema = z.object({
  id: z.string(),
  custom_id: z.string(),
  response: z.null(),
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

export type FailedBatchInferenceJSONLine = z.infer<
  typeof FailedBatchInferenceJSONLineSchema
>;

export const BatchRequestToDispatchSchema = z.object({
  requestBody: BatchInputRequestObjectSchema,
  customId: z.string(),
  requestId: z.string(),
  batchId: z.string(),
  batchSize: z.number(),
  expiresAt: z.number(),
  teamId: z.string(),
  inputFileId: z.string(),
});

export type BatchRequestToDispatch = z.infer<
  typeof BatchRequestToDispatchSchema
>;

// Batch finalization message schema
export const BatchFinalizationMessageSchema = z.object({
  batchId: z.string(),
  /**
   * Closes the batch with the lines done so far: set by the sweep for an
   * expired or cancelling batch, and by the submission consumer for a batch
   * whose submission failed after dispatching lines. Absent when every line
   * finished.
   */
  closeAs: z.enum(["expired", "cancelled", "failed"]).optional(),
  /** With `closeAs: "failed"`, the error recorded on the batch. */
  reason: z.string().optional(),
});

export type BatchFinalizationMessage = z.infer<
  typeof BatchFinalizationMessageSchema
>;

/** ******************************************************************************
 *  Group Slow Generations
 ******************************************************************************* */

export const GroupCreateCompletionRequestSchema = z
  .object({
    requests: z.array(V1ChatCompletionRequestSchema).min(1).max(50),
    webhook_id: z.string().optional(),
  })
  .strict();

export const GroupCreateResponsesRequestSchema = z
  .object({
    requests: z.array(V1ResponsesCreateRequestSchema).min(1).max(50),
    webhook_id: z.string().optional(),
  })
  .strict();

export const GroupCreateTextCompletionRequestSchema = z
  .object({
    requests: z.array(V1TextCompletionRequestSchema).min(1).max(50),
    webhook_id: z.string().optional(),
  })
  .strict();

export const GroupCreateEmbeddingRequestSchema = z
  .object({
    requests: z.array(EmbeddingRequestPayloadSchema).min(1).max(50),
    webhook_id: z.string().optional(),
  })
  .strict();

export const GroupGenerationRequestSchema =
  GroupCreateCompletionRequestSchema.or(GroupCreateResponsesRequestSchema)
    .or(GroupCreateTextCompletionRequestSchema)
    .or(GroupCreateEmbeddingRequestSchema);

export type GroupCreateCompletionRequest = z.infer<
  typeof GroupCreateCompletionRequestSchema
>;
export type GroupCreateTextCompletionRequest = z.infer<
  typeof GroupCreateTextCompletionRequestSchema
>;
export type GroupCreateEmbeddingRequest = z.infer<
  typeof GroupCreateEmbeddingRequestSchema
>;
export type GroupCreateResponsesRequest = z.infer<
  typeof GroupCreateResponsesRequestSchema
>;
export type GroupGenerationRequest = z.infer<
  typeof GroupGenerationRequestSchema
>;

export const GroupBatchFinalizedMessageSchema = z.object({
  groupId: z.string(),
  groupSize: z.number(),
  finalizedAt: z.number(),
  metadata: z.record(z.string(), z.any()).optional(),
  webhookId: z.string().optional(),
  teamId: z.string(),
});

export type GroupBatchFinalizedMessage = z.infer<
  typeof GroupBatchFinalizedMessageSchema
>;

/** ******************************************************************************
 *  Batch Stream Handler Types
 ******************************************************************************* */

// oxlint-disable-next-line typescript/no-explicit-any
export type BatchMetadata = Record<string, any> | null;

// Batch submission schema
export const BatchSubmissionSchema = z.object({
  _id: z.string(),
  teamId: z.string(),
  /** Present on newly published submissions; absent on in-flight messages. */
  projectId: z.string().optional(),
  inputFileId: z.string(),
  metadata: z.record(z.string(), z.any()).nullable().optional(),
  batchSize: z.number(),
  expiresAt: z.number(),
});

export type BatchSubmission = z.infer<typeof BatchSubmissionSchema>;

export type SuccessfulBatchInferenceResult = {
  batchId: string;
  teamId: string;
  requestId: string;
  customId: string;
  generationId: string;
  success: true;
  inferenceResult: GenerationResponse;
};

export type FailedBatchInferenceResult = {
  batchId: string;
  teamId: string;
  requestId: string;
  customId: string;
  generationId: string | null;
  error: string;
  success: false;
};

export type BatchInferenceResult =
  | SuccessfulBatchInferenceResult
  | FailedBatchInferenceResult;

export type BatchStreamSubjects =
  | "batch-submit"
  | "batch-dispatch-request"
  | "batch-inference-result"
  | "inference-slow"
  | "batch-begin-listen-for-results"
  | "batch-start-finalization";
